import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { services } from '../app';
import { moonlessSky, roundStars, starsSeen } from '../render/nakedeye';
import { panel, place, placeSource, setHome, setSetting, settings, showToast, viewMode } from '../state';
import { eyeSqm, limitNudge } from '../live/skycheck';
import { Segmented, Sheet, Toggle } from './chrome';
import { HELP } from '../data/guide';
import { APP_NAME_AR } from '../data/brand';
import { fmtNum } from './format';
import { Icon } from './icons';
import { unlockNarration } from '../audio/narrator';
import { AlertState, alertState, disableAlerts, enableAlerts, refreshAlertState, syncAlerts, testAlert } from '../live/push';
import { canPromptInstall, isIOSDevice, isStandalone, promptInstall } from '../live/install';
import { backUpNights, restoreNights } from './NightsPanel';
import { warmthWords } from './LayersPanel';
import { WhatsNewRow } from './WhatsNew';
import { keepCorrection, keptCorrection } from '../live/align';
import { beginAligning } from './Align';
import { GeoResult, usePlaceSearch } from '../live/placesearch';

const BORTLE = ['Excellent dark site', 'Truly dark sky', 'Rural sky', 'Rural to suburban', 'Suburban sky', 'Bright suburban', 'Suburban to urban', 'City sky', 'Inner city'];

const ALERT_TEXT: Record<AlertState, string> = {
  off: 'Clear evenings and bright ISS passes, even with Ahillah closed',
  on: 'On: choose what to hear about below',
  working: 'Setting up…',
  unsupported: 'This browser cannot receive notifications from web apps',
  install: 'On iPhone and iPad, first add Ahillah to your Home Screen (Share, then Add to Home Screen), then turn this on there',
  denied: 'Notifications are blocked for Ahillah in your browser or phone settings',
  unavailable: 'Alerts are not available right now; try again later',
};

/** Where this device lets Ahillah's notifications make a sound (the owner's words, 19 October 2026; the Android names are
 *  still to be checked on a real phone). */
function soundHelp(): string {
  const ua = navigator.userAgent;
  if (isIOSDevice()) return 'Settings › Notifications › Ahillah › Sounds.';
  if (/Android/.test(ua)) return 'Your phone’s settings › Apps › Ahillah › Notifications › allow sound.';
  if (/Windows/.test(ua)) return 'Settings › System › Notifications › Ahillah › Play a sound.';
  if (/Macintosh/.test(ua)) return 'System Settings › Notifications › Ahillah › Play sound for notification. Chrome and Edge on a Mac never play notification sounds.';
  return '';
}

const LICENCES: Record<string, string> = {
  'CC BY 4.0': 'https://creativecommons.org/licenses/by/4.0/',
  'CC BY-SA 4.0': 'https://creativecommons.org/licenses/by-sa/4.0/',
  'CC BY-SA 3.0': 'https://creativecommons.org/licenses/by-sa/3.0/',
  'CC BY-NC 4.0': 'https://creativecommons.org/licenses/by-nc/4.0/',
  'CC BY-NC-ND 4.0': 'https://creativecommons.org/licenses/by-nc-nd/4.0/',
  'CC BY-NC-SA 4.0': 'https://creativecommons.org/licenses/by-nc-sa/4.0/',
  'CC BY-NC 3.0 IGO': 'https://creativecommons.org/licenses/by-nc/3.0/igo/',
  'CC BY-SA 3.0 IGO': 'https://creativecommons.org/licenses/by-sa/3.0/igo/',
  'CC BY-SA': 'https://creativecommons.org/licenses/by-sa/4.0/',
  ODbL: 'https://opendatacommons.org/licenses/odbl/',
  'Free Art License': 'https://artlibre.org/licence/lal/en/',
  'BSD-3': 'https://opensource.org/license/bsd-3-clause',
  MIT: 'https://opensource.org/license/mit',
  'SIL Open Font License 1.1': 'https://openfontlicense.org/',
};
/** Credits text with each licence name linked to the licence, as the licences ask. */
function withLicenceLinks(text: string) {
  const keys = Object.keys(LICENCES).sort((a, b) => b.length - a.length).map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp(`\\b(${keys.join('|')})(?![\\w-])`, 'g');
  const out: (string | preact.JSX.Element)[] = [];
  let last = 0;
  for (const m of text.matchAll(re)) {
    out.push(text.slice(last, m.index));
    out.push(<a key={m.index} href={LICENCES[m[1]]} target="_blank" rel="noopener">{m[1]}</a>);
    last = m.index! + m[1].length;
  }
  out.push(text.slice(last));
  return out;
}

function InstallRow() {
  const [iosSteps, setIosSteps] = useState(false);
  if (isStandalone()) return null;
  if (canPromptInstall.value) {
    return (
      <button class="list-row btn-row" onClick={() => void promptInstall()}>
        <span class="row-icon"><Icon name="phone" size={18} /></span>
        <span class="row-text"><span class="row-label">Install Ahillah</span><span class="row-sub">Full screen, works offline, and allows clear-sky alerts</span></span>
        <Icon name="chevron" size={16} />
      </button>
    );
  }
  if (!isIOSDevice()) return null;
  return (
    <>
      <button class="list-row btn-row" aria-expanded={iosSteps} onClick={() => setIosSteps(!iosSteps)}>
        <span class="row-icon"><Icon name="phone" size={18} /></span>
        <span class="row-text"><span class="row-label">Add Ahillah to your Home Screen</span><span class="row-sub">Full screen, works offline, and allows clear-sky alerts</span></span>
        <Icon name="chevron" size={16} />
      </button>
      {iosSteps && (
        <ol class="install-steps">
          <li>Tap the Share button (the square with an arrow pointing up). If you can’t see it, look in the browser’s menu (often three dots).</li>
          <li>Scroll down and tap <strong>Add to Home Screen</strong>, then <strong>Add</strong>.</li>
          <li>Open Ahillah from its new icon. Turn on clear-sky alerts there if you would like them.</li>
        </ol>
      )}
    </>
  );
}

function PushAlerts() {
  useEffect(() => void refreshAlertState(), []);
  const [note, setNote] = useState<string | null>(null);
  const st = alertState.value;
  const s = settings.value;
  const pref = (k: 'pushEvening' | 'pushIss' | 'pushEvents' | 'pushSought' | 'pushMonths' | 'pushWhiteDays' | 'pushSound', v: boolean) => {
    setSetting(k, v);
    void syncAlerts(true);
  };
  return (
    <>
      <Toggle on={st === 'on'} onChange={(v) => void (v ? enableAlerts() : disableAlerts())} icon="bell" label="Notifications" sub={ALERT_TEXT[st]} />
      {st === 'off' && (
        <div class="row-sub pad-x">To send them, Ahillah’s server keeps your notification address, your location to about 10 km, your time zone, the times of the next few ISS passes and sky events, and which month alerts you chose, until you turn this off.</div>
      )}
      {st === 'on' && (
        <div class="sub-group">
          <Toggle on={s.pushEvening} onChange={(v) => pref('pushEvening', v)} label="Clear evenings" sub="About 4 pm, when tonight’s forecast shows at least two clear hours" />
          <Toggle on={s.pushIss} onChange={(v) => pref('pushIss', v)} label="ISS passes" sub="Ten minutes before each bright pass, unless it is overcast. Open Ahillah every few days to keep these coming" />
          <Toggle on={s.pushEvents} onChange={(v) => pref('pushEvents', v)} label="Sky events" sub="The afternoon before a meteor shower’s peak, a close pairing, a planet at its brightest or a supermoon (unless the night looks overcast); and the day or evening before an eclipse you can see, and an hour before" />
          <Toggle on={s.pushSought} onChange={(v) => pref('pushSought', v)} label={'Ramaḍān, Shawwāl and Dhū al-Ḥijjah'} sub="When Saudi Arabia looks for the crescent, and when it announces the new month" />
          <Toggle on={s.pushMonths} onChange={(v) => pref('pushMonths', v)} label="Every other month" sub="The day before each new month begins, by the Umm al-Qurá calendar. If Saudi Arabia’s Supreme Court later decides otherwise, Ahillah’s date follows the court" />
          <Toggle on={s.pushWhiteDays} onChange={(v) => pref('pushWhiteDays', v)} label="The white days" sub="On the afternoon of the 12th of each month, except in Ramaḍān and Dhū al-Ḥijjah: the 13th, 14th and 15th come next" />
          <Toggle on={s.pushSound} onChange={(v) => pref('pushSound', v)} label="Play a sound" sub="Notifications come silently unless you switch this on. Your phone or computer must also allow sound for Ahillah." />
          {s.pushSound && soundHelp() && <div class="row-sub pad-x">{soundHelp()}</div>}
          <button class="btn-ghost small test-alert" onClick={async () => setNote(await testAlert())}>Send a test alert</button>
          {note && <div class="fine" role="status">{note}</div>}
        </div>
      )}
    </>
  );
}

/** Align from Settings: in pointing mode, over the sky straight away; otherwise, how to get there first. */
function alignFromSettings() {
  if (viewMode.peek() !== 'ar') return showToast('Tap Point at the bottom first, then Align.', 4500);
  panel.value = null;
  beginAligning();
}


export function SettingsPanel() {
  const s = settings.value;
  // Tonight's correction from Align, if there is one (src/live/align.ts); undone here, the rows change at once.
  const [kept, setKept] = useState(() => keptCorrection());
  const undoAlignment = () => {
    const o = services.peek()?.engine.orientation;
    if (o) o.calibration = 0;
    keepCorrection(0);
    setKept(0);
    showToast('Alignment undone');
  };
  // Seven taps on "About Ahillah" within four seconds: the owner's own page (src/ui/OwnerPanel.tsx).
  const taps = useRef<number[]>([]);
  const aboutTap = () => {
    const now = Date.now();
    taps.current = [...taps.current.filter((t) => now - t < 4000), now];
    if (taps.current.length >= 7) {
      taps.current = [];
      panel.value = 'owner';
    }
  };
  const eng = services.value?.engine;
  // The stars the eye picks out at once on a clear night with no Moon, counted as Naked eye counts them (its limit where
  // each part of the sky is, less the air there, with the darkness check's fit): a plainer measure of a sky than its
  // Bortle class or limiting magnitude.
  const sqm = eyeSqm(place.value, s);
  const nudge = limitNudge.value;
  const starsVisible = useMemo(() => {
    const mags = eng?.stars.mag;
    if (!mags) return '';
    return roundStars(starsSeen(moonlessSky(s.bortle, sqm, nudge), mags)).toLocaleString('en-GB');
  }, [s.bortle, sqm, nudge, eng]);
  return (
    <Sheet onClose={() => (panel.value = null)} size="tall" label="Settings">
      <h2 class="sheet-title">Settings</h2>
      <div class="group">
        <InstallRow />
        <button class="list-row btn-row" onClick={() => (panel.value = 'help')}>
          <span class="row-icon"><Icon name="help" size={18} stroke={1.9} /></span>
          <span class="row-text"><span class="row-label">{HELP.title}</span><span class="row-sub">{HELP.settingsSub}</span></span>
          <Icon name="chevron" size={16} />
        </button>
        <WhatsNewRow />
        <button class="list-row btn-row" onClick={() => (panel.value = 'location')}>
          <span class="row-icon"><Icon name="pin" size={18} /></span>
          <span class="row-text"><span class="row-label">Location</span><span class="row-sub">{place.value.name} · {fmtNum(place.value.lat, 3)}°, {fmtNum(place.value.lon, 3)}°</span></span>
          <Icon name="chevron" size={16} />
        </button>
        <button class="list-row btn-row" onClick={() => (panel.value = 'layers')}>
          <span class="row-icon"><Icon name="nightEye" size={18} /></span>
          <span class="row-text"><span class="row-label">Night vision</span><span class="row-sub">{s.nightMode ? `On · ${warmthWords(s.nightWarmth ?? 0.5)}` : 'Off'} · set in Sky view</span></span>
          <Icon name="chevron" size={16} />
        </button>
      </div>
      <div class="group">
        <div class="group-title">Your sky</div>
        <Toggle on={s.bortleAuto} onChange={(v) => setSetting('bortleAuto', v)} icon="pin" label="Light pollution from my location"
          sub={s.bortleAuto ? (s.sqm != null ? `${BORTLE[s.bortle - 1]} (Bortle ${s.bortle}), from a light-pollution atlas. Turn off to set it yourself` : 'Looking it up…') : 'Off: choose how dark your sky is below'} />
        {!s.bortleAuto && (
          <div class="slider-row">
            <div class="slider-head"><label for="sky-dark">{BORTLE[s.bortle - 1]}</label><span class="muted tnum">Bortle {s.bortle}</span></div>
            <input id="sky-dark" type="range" min={1} max={9} step={1} value={s.bortle} aria-valuetext={`${BORTLE[s.bortle - 1]}, Bortle ${s.bortle}`}
              onInput={(e) => setSetting('bortle', Number((e.target as HTMLInputElement).value))} />
            <div class="slider-scale"><span>Darkest countryside</span><span>City centre</span></div>
          </div>
        )}
        <div class="row-sub pad-x">
          {starsVisible ? `On a clear night with no Moon, about ${starsVisible} stars are visible from here at once. ` : ''}This sets how real the sky looks and how many meteors to expect.
        </div>
      </div>
      <div class="group">
        <div class="group-title">Alerts</div>
        <Toggle on={s.alerts} onChange={(v) => setSetting('alerts', v)} icon="satellite" label="Satellite banners" sub="While Ahillah is open: a banner when the ISS or another bright satellite comes into view" />
        {'vibrate' in navigator && (
          <Toggle on={s.alertVibrate} onChange={(v) => { setSetting('alertVibrate', v); if (v) navigator.vibrate?.([120, 80, 120]); }} icon="bell" label="Vibrate with banners" sub="A short buzz as well" />
        )}
        <PushAlerts />
      </div>
      <div class="group">
        <div class="group-title">Pointing mode</div>
        <Toggle on={s.startPointing} onChange={(v) => setSetting('startPointing', v)} icon="phone" label="Open in pointing mode" sub="On a phone, Ahillah follows where you point from the start. Drag with a finger to look around; move the phone and it follows again." />
        <Toggle on={s.sayPointing} onChange={(v) => { if (v) unlockNarration(); setSetting('sayPointing', v); }} icon="speaker" label="Say what I point at" sub="A voice names the star, planet or satellite in the circle" />
        <Toggle on={s.cameraFeed} onChange={(v) => setSetting('cameraFeed', v)} icon="camera" label="Camera behind the sky" sub="The sky drawn over what your camera sees" />
        {s.cameraFeed && (
          <div class="slider-row">
            <div class="slider-head"><label for="camera-fov">Camera width</label><span class="muted tnum">{s.cameraFov}°</span></div>
            <input id="camera-fov" type="range" min={30} max={90} step={1} value={s.cameraFov} onInput={(e) => setSetting('cameraFov', Number((e.target as HTMLInputElement).value))} aria-valuetext={`${s.cameraFov} degrees`} />
            <div class="row-sub">Adjust until the drawn stars sit on the real ones.</div>
          </div>
        )}
        <button class="list-row btn-row" onClick={alignFromSettings}>
          <span class="row-icon"><Icon name="compass" size={18} /></span>
          <span class="row-text"><span class="row-label">Align with the sky</span><span class="row-sub">{kept ? 'Aligned tonight. Tap to do it again.' : 'If the sky looks turned, line it up with the Moon, a bright planet or a bright star'}</span></span>
          <Icon name="chevron" size={16} />
        </button>
        {kept !== 0 && (
          <button class="list-row btn-row" onClick={undoAlignment}>
            <span class="row-icon"><Icon name="close" size={16} /></span>
            <span class="row-text"><span class="row-label">Undo tonight’s alignment</span><span class="row-sub">Back to the compass alone</span></span>
          </button>
        )}
      </div>
      <div class="group">
        <div class="group-title">Arabic</div>
        <Toggle on={s.arabic} onChange={(v) => setSetting('arabic', v)} icon="star" label="Arabic names" sub="The Arabic names of the stars, the planets, the Sun and Moon and more, on their cards and in search, and the Arabic of the hadith and the scholars’ words." />
        <div class="seg-row">
          <span class="row-label-inline">Arabic voice</span>
          <Segmented label="Arabic voice" value={s.arabicVoice} options={[['m', 'Man'], ['f', 'Woman']]} onChange={(v) => setSetting('arabicVoice', v)} />
        </div>
        <div class="row-sub pad-x">Reads the Arabic names, hadith and scholars’ words aloud: tap the speaker beside them.</div>
      </div>
      <div class="group">
        <div class="group-title">Your nights</div>
        <button class="list-row btn-row" onClick={() => void backUpNights()}>
          <span class="row-icon"><Icon name="download" size={18} /></span>
          <span class="row-text"><span class="row-label">Back up your nights</span><span class="row-sub">Save them as a file, to Files, a cloud drive or an email. They are kept on this phone only, and deleting Ahillah deletes them.</span></span>
        </button>
        <label class="list-row btn-row">
          <span class="row-icon"><Icon name="upload" size={18} /></span>
          <span class="row-text"><span class="row-label">Restore from a backup</span><span class="row-sub">Adds the nights in a backup file to the ones here</span></span>
          <input
            type="file" accept="application/json,.json" class="sr-only"
            onChange={(e) => {
              const input = e.target as HTMLInputElement;
              const f = input.files?.[0];
              if (f) restoreNights(f);
              input.value = '';
            }}
          />
        </label>
      </div>
      <div class="group about">
        <div class="group-title" onClick={aboutTap}>About Ahillah</div>
        <p>
          Ahillah (<span class="arabic-script" lang="ar" dir="rtl">{APP_NAME_AR}</span>) is made by Abu Ruqayyah <span class="no-break">Abdus-Samad</span> Khalid. The sky is worked out on your phone. Only a rounded location leaves it, for the
          weather, and nothing about you is kept unless you turn on alerts.
        </p>
        <button class="list-row btn-row" onClick={() => (panel.value = 'name')}>
          <span class="row-icon"><Icon name="moon" size={18} /></span>
          <span class="row-text"><span class="row-label">Why Ahillah?</span><span class="row-sub">What the name means, from the Quran and the scholars</span></span>
          <Icon name="chevron" size={16} />
        </button>
        {/* The rest stays one tap away: the sources' licences ask for their credit (and some for a link to the licence). */}
        <details class="about-more">
          <summary>Privacy, sources and licences <Icon name="chevron" size={16} /></summary>
          <p>
            Apart from clear-sky alerts and feedback, only a rounded location leaves your phone: to about 1 km for the weather forecast
            (Open-Meteo). Nothing is stored about you, except if you turn on clear-sky alerts or send feedback. With alerts, Ahillah’s server
            keeps your notification address, your location to about 10 km and your time zone, only to send the alerts; turning alerts off
            deletes them. Feedback you send is kept for a year, only to improve Ahillah, with what goes with it: when you wrote it, your phone
            type, screen size, language and time zone, Ahillah’s version and settings, what the sky and the compass showed, the last few things
            you looked at, any errors Ahillah met and your location to about 10 km. Your nights (what
            you tick “Seen it” on) and your darkness checks are kept on this phone only. Pictures and news in Learn are fetched from ESA, ESO,
            NOIRLab and NASA when you open it. When you tap the sky photographs while zoomed in, the sky position you tapped (not where you are) is
            sent to SIMBAD to identify what is there.
          </p>
          <p class="fine">{withLicenceLinks(
            `Objects identified by tapping the sky photographs: SIMBAD database, CDS, Strasbourg (Wenger et al. 2000). Stars: HYG database v4.4 (David Nash, CC BY-SA 4.0); fainter than magnitude 8, Tycho-2 (Høg et al. 2000) and Hipparcos (ESA 1997), credit ESA, via CDS Strasbourg (CC BY-NC 3.0 IGO). Fainter still, when zoomed in: Gaia DR3 (ESA/Gaia/DPAC, CC BY-NC 3.0 IGO), kept in separate files with their own notice; this work has made use of data from the European Space Agency mission Gaia, processed by the Gaia Data Processing and Analysis Consortium, funded by national institutions, in particular those in the Gaia Multilateral Agreement. Constellation figures and borders: d3-celestial (Olaf Frohn, BSD-3). Deep-sky objects: OpenNGC (Mattia Verga, CC BY-SA 4.0), with the brightness of globular clusters from the Harris catalogue (W. E. Harris 1996, 2010 edition, physics.mcmaster.ca). Milky Way and Moon maps: NASA Goddard Scientific Visualization Studio (Gaia DR2: ESA/Gaia/DPAC; Moon: LRO colour and LOLA heights, CGI Moon Kit). Sky photos when zoomed in: the Digitized Sky Surveys, produced at the Space Telescope Science Institute under US Government grant NAG W-2166 from plates taken with the Oschin Schmidt Telescope on Palomar Mountain and the UK Schmidt Telescope, and processed with the permission of these institutions; digitised images © California Institute of Technology, the Anglo-Australian Observatory Board, UK SERC/PPARC with the Anglo-Australian Telescope Board, and AURA, distributed by agreement and used with permission; colour HiPS by CDS Strasbourg. Most Messier photos on the cards: Pan-STARRS1 DR1 colour (PS1 Science Consortium, STScI); the Pan-STARRS1 Surveys (PS1) and the PS1 public science archive have been made possible through contributions by the Institute for Astronomy, the University of Hawaii, the Pan-STARRS Project Office, the Max-Planck Society and its participating institutes, the Max Planck Institute for Astronomy, Heidelberg and the Max Planck Institute for Extraterrestrial Physics, Garching, The Johns Hopkins University, Durham University, the University of Edinburgh, the Queen’s University Belfast, the Harvard-Smithsonian Center for Astrophysics, the Las Cumbres Observatory Global Telescope Network Incorporated, the National Central University of Taiwan, the Space Telescope Science Institute, the National Aeronautics and Space Administration under Grant No. NNX08AR22G issued through the Planetary Science Division of the NASA Science Mission Directorate, the National Science Foundation Grant No. AST-1238877, the University of Maryland, Eotvos Lorand University (ELTE), the Los Alamos National Laboratory, and the Gordon and Betty Moore Foundation. In the far south, SkyMapper Southern Survey DR4 (SkyMapper team, Australian National University; Onken et al. 2024; CC BY 4.0). Cutouts by CDS Strasbourg; contains information from the CDS HiPS database, made available under the ODbL. Messier photos of large, bright objects, from observatories (each CC BY 4.0, cropped and turned north up): M1, KPNO/NOIRLab/NSF/AURA; Image Processing: T.A. Rector (University of Alaska Anchorage/NSF NOIRLab); M7, M8, M16 and M33, ESO; M17, ESO/INAF-VST/OmegaCAM. Acknowledgement: OmegaCen/Astro-WISE/Kapteyn Institute; M20, NSF–DOE Vera C. Rubin Observatory/NOIRLab/SLAC/AURA; M24, Vanessa Harvey, REU program/NOIRLab/NSF/AURA; M31, Bill Schoening, Vanessa Harvey/REU program/NOIRLab/NSF/AURA; M42 and M43, ESO/G. Beccari; M44, KPNO/NOIRLab/NSF/AURA/Tom Bash and John Fox/Adam Block; M45, KPNO/NOIRLab/NSF/AURA/Tad Denton/Adam Block. Gallery: pictures from ESA/Webb, ESA/Hubble, ESO and NOIRLab (CC BY 4.0), from NASA, from ESA (used with its permission), from other space agencies, observatories, photographers and image processors (with their permission, or under the Creative Commons licence each names) and historic photographs in the public domain, each credited where it is shown; resized for the app, and cropped where its credit says so. Planets: Astronomy Engine (Don Cross, MIT). Planet maps: Solar System Scope (CC BY 4.0). Satellites: CelesTrak (Dr T.S. Kelso) with satellite.js; brightness from Mike McCants’ qs.mag. Meteor showers: International Meteor Organization 2026 calendar. Weather: Open-Meteo (CC BY 4.0), using the Met Office model in the UK (Met Office data CC BY-SA 4.0); chance of clear sky from the ECMWF, DWD ICON-EU (NOAA GEFS outside Europe) and Met Office MOGREPS-G ensembles; haze from Copernicus Atmosphere Monitoring Service aerosol forecasts; seeing from NOAA GFS with the AXP model (Trinquet and Vernin 2006; Ye 2011), calibrated against the ESO Paranal Astronomical Site Monitor (ESO, CC BY 4.0) and Maunakea Weather Center seeing records. Space weather: NOAA SWPC (Kp forecast and the OVATION aurora model). Magnetic model: NOAA/BGS WMM2025. Sky brightness: World Atlas of the Artificial Night Sky Brightness (Falchi et al. 2016, CC BY-NC 4.0). Stargazing places: © OpenStreetMap contributors (ODbL); International Dark Sky Places (Spinner et al. 2024, CC BY 4.0). Other wavelengths: radio, the WMAP K-band map at 23 GHz, and microwave, the WMAP W-band and ILC maps, NASA/WMAP Science Team; far infrared, IRAS (NASA/JPL-Caltech/IPAC) and Miville-Deschênes and Lagache 2005; hydrogen-alpha, Finkbeiner 2003; gamma rays, NASA/DOE/Fermi LAT Collaboration; all via NASA LAMBDA and SkyView. Comets: Minor Planet Center. Asteroids and close approaches: NASA/JPL Small-Body Database. The Quran: the Uthmanic Hafs text of the King Fahd Glorious Quran Printing Complex, Madīnah. Hadith, their gradings and the scholars’ words: from the books named with each; English translations made for Ahillah. Honorific signs: Scheherazade New (SIL Global, SIL Open Font License 1.1). Arabic type: Adwa as-Salaf (used with permission); the Quran in the Complex’s Uthmanic Hafs font. Constellation histories: Ian Ridpath, Star Tales; J. H. Rogers (1998). Narration voice: George, from Kokoro-82M (hexgrad, Apache 2.0), read by Qwen3-TTS (Qwen team, Alibaba Cloud, Apache 2.0). The sky calendar film: the Earth from NASA Earth Observatory’s Blue Marble Next Generation (Reto Stöckli) and Black Marble 2016 (Suomi NPP VIIRS); light pollution from the World Atlas above. The introduction film: Hubble’s 2019 portrait of Saturn (NASA, ESA, A. Simon (Goddard Space Flight Center), and M.H. Wong (University of California, Berkeley); CC BY 4.0) and Webb’s pillars of the Eagle Nebula (NASA, ESA, CSA, STScI; J. DePasquale, A. Koekemoer, A. Pagan (STScI); CC BY 4.0), with Ahillah’s own sky and screens, and the Moon from the maps above. Size comparisons (How big are they?): photographs from NASA, from ESA/Hubble (CC BY 4.0) and from ESA and JAXA (used with their permission), each credited on its board, cut out and resized; sizes from NASA’s planetary fact sheets, JPL and the papers named there.`,
          )}</p>
        </details>
      </div>
    </Sheet>
  );
}

export function LocationPanel() {
  const [q, setQ] = useState('');
  const { results, status } = usePlaceSearch(q);
  const [busy, setBusy] = useState(false);
  const useGps = () => {
    if (!navigator.geolocation) return showToast('Location is not available in this browser.');
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setBusy(false);
        setHome({
          lat: pos.coords.latitude, lon: pos.coords.longitude, height: pos.coords.altitude ?? 30,
          name: 'My location', timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        placeSource.value = 'gps';
        panel.value = null;
        showToast('Location updated');
      },
      (err) => {
        setBusy(false);
        showToast(err.code === 1 ? 'Location permission was declined. You can search for a place instead.' : 'Could not get your location. Try again or search for a place.', 5000);
      },
      { enableHighAccuracy: false, timeout: 12000, maximumAge: 10 * 60e3 },
    );
  };
  const choose = (g: GeoResult) => {
    setHome({ lat: g.latitude, lon: g.longitude, height: g.elevation ?? 20, name: g.name, timeZone: g.timezone });
    placeSource.value = 'manual';
    panel.value = null;
  };
  return (
    <Sheet onClose={() => (panel.value = null)} size="tall" label="Location">
      <h2 class="sheet-title">Where are you?</h2>
      <button class="btn" onClick={useGps} disabled={busy}>
        <Icon name="locate" size={18} /> {busy ? 'Finding you…' : 'Use my current location'}
      </button>
      <div class="search-box">
        <Icon name="search" size={18} />
        <input value={q} onInput={(e) => setQ((e.target as HTMLInputElement).value)} placeholder="Search for a town or city" autocomplete="off" />
      </div>
      {status === 'none' && <div class="muted small pad">No places found for “{q.trim()}”. Try the nearest town.</div>}
      {status === 'failed' && <div class="muted small pad">{navigator.onLine ? 'Place search is not responding. Try again in a moment.' : 'Place search needs a connection. You can still use your current location.'}</div>}
      <div class="results">
        {results.map((g) => (
          <button class="result" key={`${g.latitude},${g.longitude}`} onClick={() => choose(g)}>
            <span class="result-icon"><Icon name="pin" size={18} /></span>
            <span class="result-text">
              <span class="result-label">{g.name}</span>
              <span class="result-sub">{[g.admin1, g.country].filter(Boolean).join(', ')}</span>
            </span>
          </button>
        ))}
      </div>
      <p class="fine">Place search by Open-Meteo. Current: {place.value.name} ({fmtNum(place.value.lat, 3)}°, {fmtNum(place.value.lon, 3)}°){place.value.timeZone ? ` · ${place.value.timeZone}` : ''}.</p>
    </Sheet>
  );
}
